import React, { useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./MainPage.css";
import HeroSection from "./MainPageComponents/HeroSection/HeroSection";
import FeaturesSection from "./MainPageComponents/FeaturesSection/FeaturesSection";
import SolutionsSection from "./MainPageComponents/SolutionsSection/SolutionsSection";
import BenefitsSection from "./MainPageComponents/BenefitsSection/BenefitsSection";
import PricingSection from "./MainPageComponents/PricingSection/PricingSection";
import ContactSection from "./MainPageComponents/ContactSection/ContactSection";
import ExplanationSection from "./MainPageComponents/ExplanationSection/ExplanationSection";
import BusinessGainsSection from "./MainPageComponents/BusinessGainsSection/BusinessGainsSection";
import CTASection from "./MainPageComponents/CTASection/CTASection";

// Registrer ScrollTrigger
gsap.registerPlugin(ScrollTrigger);

const MainPage = () => {
  useEffect(() => {
    // Animation d'apparition progressive des sections
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    // Observer toutes les sections avec la classe fade-in-section
    const sections = document.querySelectorAll('.fade-in-section');
    sections.forEach(section => observer.observe(section));

    // Créer la barre de scroll personnalisée
    const createProgressScrollBar = () => {
      // Créer les éléments de la barre de scroll
      const progressBar = document.createElement('div');
      progressBar.className = 'progress-scroll-bar';
      
      const progressThumb = document.createElement('div');
      progressThumb.className = 'progress-scroll-thumb';
      
      progressBar.appendChild(progressThumb);
      document.body.appendChild(progressBar);

      // Animer la barre de scroll avec GSAP ScrollTrigger
      gsap.to(progressThumb, {
        height: "100%",
        ease: "none",
        scrollTrigger: {
          trigger: document.body,
          start: "top top",
          end: "bottom bottom",
          scrub: 0.3,
          onUpdate: (self) => {
            // Mettre à jour la hauteur du thumb basé sur le progrès
            const progress = self.progress;
            gsap.set(progressThumb, { 
              height: `${progress * 100}%`,
              transformOrigin: "top"
            });
          }
        }
      });

      // Effet hover interactif
      progressBar.addEventListener('mouseenter', () => {
        gsap.to(progressBar, {
          width: '8px',
          duration: 0.3,
          ease: "power2.out"
        });
      });

      progressBar.addEventListener('mouseleave', () => {
        gsap.to(progressBar, {
          width: '6px',
          duration: 0.3,
          ease: "power2.out"
        });
      });

      // Navigation au clic - scroll fluide
      progressBar.addEventListener('click', (e) => {
        const rect = progressBar.getBoundingClientRect();
        const clickY = e.clientY - rect.top;
        const percentage = clickY / rect.height;
        const targetY = percentage * (document.body.scrollHeight - window.innerHeight);
        
        // Animation de scroll fluide sans plugin
        window.scrollTo({
          top: targetY,
          behavior: 'smooth'
        });
      });

      return progressBar;
    };

    // Créer la barre de scroll
    const progressBar = createProgressScrollBar();

    // Animation des sections individuelles
    gsap.utils.toArray('.fade-in-section').forEach((section, index) => {
      gsap.fromTo(section, 
        {
          opacity: 0,
          y: 50
        },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          }
        }
      );
    });

    // Cleanup
    return () => {
      sections.forEach(section => observer.unobserve(section));
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      if (progressBar && progressBar.parentNode) {
        progressBar.parentNode.removeChild(progressBar);
      }
    };
  }, []);

  return (
    <div className="main-page-wrapper">
      <div className="main-page-root-container">
        <div className="main-page">
          {/* Section Hero */}
          <section className="fade-in-section">
            <HeroSection />
          </section>

          {/* Section Fonctionnalités */}
          <section className="fade-in-section">
            <FeaturesSection />
          </section>

          {/* Section Explications */}
          <section className="fade-in-section">
            <ExplanationSection />
          </section>

          {/* Section Avantages */}
          <section className="fade-in-section">
            <BenefitsSection />
          </section>

          {/* Section Gains Business */}
          <section className="fade-in-section">
            <BusinessGainsSection />
          </section>

          {/* Section Solutions */}
          <section className="fade-in-section">
            <SolutionsSection />
          </section>

          {/* Section Tarifs */}
          <section className="fade-in-section">
            <PricingSection />
          </section>

          {/* Section CTA */}
          <section className="fade-in-section">
            <CTASection />
          </section>

          {/* Section Contact */}
          <section className="fade-in-section">
            <ContactSection />
          </section>
        </div>
      </div>
    </div>
  );
};

export default MainPage;
